import { useEffect, useState } from 'react';
import categories from './data/categories';
import {
  predefinedColors,
  styles,
  modes,
  lightings,
  colorFilters
} from './data/constants';

import Sidebar from './components/Sidebar';
import ColorPicker from './components/ColorPicker';
import OptionsGrid from './components/OptionsGrid';
import CategoryOptions from './components/CategoryOptions';
import OutputPanel from './components/OutputPanel';
import MobileHeader from "./components/MobileHeader";
import Footer from './components/Footer';
import { buildPrompt } from './utils/buildPrompt';
import useTheme from './hooks/useTheme';

export default function App() {
  const { theme, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedMode, setSelectedMode] = useState('');
  const [selectedLighting, setSelectedLighting] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('');
  const [specificSelections, setSpecificSelections] = useState({});
  const [customPrompt, setCustomPrompt] = useState('');
  const [output, setOutput] = useState({ text: '', json: '' });

  const generate = () => {
    const text = buildPrompt({
      selectedCategory,
      selectedColor,
      selectedStyle,
      selectedMode,
      selectedLighting,
      selectedFilter,
      specificSelections,
      customPrompt
    });

    setOutput({
      text,
      json: JSON.stringify({
        prompt: text,
        parameters: {
          category: selectedCategory?.name,
          color: selectedColor,
          style: selectedStyle,
          mode: selectedMode,
          lighting: selectedLighting,
          filter: selectedFilter,
          specific: specificSelections,
          custom: customPrompt
        }
      }, null, 2)
    });
  };

  useEffect(() => {
    setCustomPrompt('');
    setSpecificSelections({});
    setOutput({ text: '', json: '' });
  }, [selectedCategory]);

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-dark-bg">
      {/* MOBILE OVERLAY */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setIsMenuOpen(false)}
        />
      )}

      <Sidebar
        categories={categories}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      <div className="flex-1 flex flex-col overflow-hidden">

        {/* MOBILE HEADER */}
        <MobileHeader
          theme={theme}
          toggleTheme={toggleTheme}
          onMenu={() => setIsMenuOpen(true)}
        />

        <main className="flex-1 p-8 overflow-y-auto">
          {!selectedCategory && <h2>Select a category</h2>}

          {selectedCategory && (
            <>
              <ColorPicker colors={predefinedColors} value={selectedColor} onChange={setSelectedColor} />
              <OptionsGrid title="Style" options={styles} value={selectedStyle} onChange={setSelectedStyle} />
              <OptionsGrid title="Mode" options={modes} value={selectedMode} onChange={setSelectedMode} />
              <OptionsGrid title="Lighting" options={lightings} value={selectedLighting} onChange={setSelectedLighting} />
              <OptionsGrid title="Filter" options={colorFilters} value={selectedFilter} onChange={setSelectedFilter} />

              <CategoryOptions
                category={selectedCategory}
                values={specificSelections}
                onChange={setSpecificSelections}
              />

              <textarea
                className="w-full h-32 mt-6 p-4 rounded-lg bg-gray-200 dark:bg-dark-card"
                placeholder="Custom instructions..."
                value={customPrompt}
                onChange={e => setCustomPrompt(e.target.value)}
              />

              <button onClick={generate} className="mt-6 px-8 py-4 bg-indigo-600 text-white rounded-lg">
                Generate Prompt
              </button>

              <OutputPanel output={output} />

              <Footer />
            </>
          )}
        </main>
      </div>
    </div>
  );
}
